import { useMemo, useState } from 'react';
import { formatMoney } from '@/utils/format';
import { createMerchant, loadMerchants, persistMerchants, toggleMerchantFavourite } from '@/utils/merchantStore';
import type { FundingWallet, Merchant, SinglePayment } from '@/types/portal';
import type { PaymentSentMeta } from '../types';
import GoodsFormStep from './GoodsFormStep';
import GoodsConfirmStep from './GoodsConfirmStep';
import GoodsFavouritesModal from './GoodsFavouritesModal';
import { imageUrl } from '@/utils/assets';

type GoodsView = 'form' | 'confirm';

interface BuyGoodsSheetProps {
  wallets: FundingWallet[];
  canManage: boolean;
  canApprove: boolean;
  counter: number;
  onPaymentSent: (payment: SinglePayment, meta: PaymentSentMeta) => void;
  onClose: () => void;
  /** A till number to prefill, e.g. from a deep link. */
  initialTill?: string | null;
}

/** Excise/interest charged on a merchant settlement, as a share of the amount. */
const INTEREST_RATE = 0.01;

const GOODS_AVATAR = imageUrl('payments/buy-goods-merchant.webp');

/**
 * Dedicated "Buy Goods" sheet. Captures a merchant till number and amount, then
 * shows a "Pay to Buy Goods" confirmation before submitting. The host page owns
 * open/close state; this component owns the two-screen stack.
 */
export default function BuyGoodsSheet({
  wallets,
  canManage,
  canApprove,
  counter,
  onPaymentSent,
  onClose,
  initialTill = null,
}: BuyGoodsSheetProps) {
  const [view, setView] = useState<GoodsView>('form');
  const [merchants, setMerchants] = useState<Merchant[]>(() => loadMerchants());
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  const [till, setTill] = useState(initialTill ?? '');
  const [amount, setAmount] = useState(0);
  const [walletId, setWalletId] = useState(
    () => wallets.find((wallet) => wallet.currency === 'KES')?.id ?? wallets[0]?.id ?? '',
  );

  const favourites = useMemo(
    () => merchants.filter((merchant) => merchant.favourite),
    [merchants],
  );
  const matchedMerchant = merchants.find((merchant) => merchant.till === till) ?? null;
  const wallet = wallets.find((item) => item.id === walletId) ?? null;
  const money = wallet?.currency ?? 'KES';

  const merchantName = matchedMerchant?.name ?? (till ? `Till ${till}` : 'Merchant');
  const mark = matchedMerchant?.mark ?? (till ? till.slice(0, 2) : '—');

  const transactionCost = 0;
  const interestFee = amount > 0 ? Math.round(amount * INTEREST_RATE * 100) / 100 : 0;
  const total = amount + interestFee;
  const shortfall = wallet ? Math.max(0, total - wallet.balance) : 0;
  const covered = wallet !== null && shortfall <= 0;

  const tillValid = /^\d{5,7}$/.test(till);
  const amountValid = amount > 0;

  const error = useMemo(() => {
    if (!canManage) return '';
    if (!wallet) return 'Select a wallet to pay from.';
    if (!tillValid) return 'Enter a valid till number (5–7 digits).';
    if (!amountValid) return 'Enter the amount you want to pay.';
    if (!covered) return `Funding wallet is short by ${formatMoney(shortfall, money)}.`;
    return '';
  }, [canManage, wallet, tillValid, amountValid, covered, shortfall, money]);

  const canContinue = canManage && error === '';

  const handleSelectMerchant = (merchant: Merchant) => {
    setSelectedMerchantId(merchant.id);
    setTill(merchant.till);
  };

  const handleToggleFavourite = (id: string) => {
    setMerchants((prev) => {
      const next = toggleMerchantFavourite(prev, id);
      persistMerchants(next);
      return next;
    });
  };

  const handleAddMerchant = (name: string, till: string) => {
    setMerchants((prev) => {
      if (prev.some((merchant) => merchant.till === till)) return prev;
      const next = [...prev, createMerchant(name, till)];
      persistMerchants(next);
      return next;
    });
  };

  const handlePay = () => {
    if (!wallet || !canContinue) return;
    const status = canApprove ? 'Paid' : 'Awaiting approval';
    const payment: SinglePayment = {
      id: `sp-${counter}`,
      reference: `BYG-${counter}`,
      payeeName: merchantName,
      avatar: GOODS_AVATAR,
      channel: 'goods',
      target: `Till ${till}`,
      amount,
      currency: money,
      rail: `Buy Goods · ${money}`,
      status,
      createdAt: '16 Sep 2026',
      createdBy: 'Peter Kironji',
    };
    onPaymentSent(payment, { newPayee: null, request: { sourceWalletId: wallet.id, destination: { till } } });
  };

  return (
    <div>
      {/* Sheet header */}
      <div className="mb-6 flex items-center gap-3 border-b border-background-200 pb-4">
        {view === 'confirm' ? (
          <button
            type="button"
            onClick={() => setView('form')}
            aria-label="Back"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        ) : null}
        <h2 className="flex-1 font-heading text-base font-semibold text-foreground-950">
          {view === 'confirm' ? 'Confirm' : 'Buy Goods'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {view === 'form' ? (
        <GoodsFormStep
          favourites={favourites}
          selectedMerchantId={selectedMerchantId}
          onSelectMerchant={handleSelectMerchant}
          onManageFavourites={() => setManageOpen(true)}
          till={till}
          onTill={setTill}
          merchantName={merchantName}
          amount={amount}
          onAmount={setAmount}
          currency={money}
          wallets={wallets}
          walletId={walletId}
          onWallet={setWalletId}
          fee={transactionCost}
          total={total}
          error={error}
          canManage={canManage}
          canContinue={canContinue}
          onContinue={() => setView('confirm')}
        />
      ) : (
        <GoodsConfirmStep
          mark={mark}
          till={till}
          merchantName={merchantName}
          amount={amount}
          currency={money}
          transactionCost={transactionCost}
          interestFee={interestFee}
          total={total}
          wallet={wallet}
          canApprove={canApprove}
          onPay={handlePay}
        />
      )}

      <GoodsFavouritesModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        merchants={merchants}
        onToggle={handleToggleFavourite}
        onAdd={handleAddMerchant}
      />
    </div>
  );
}